import { useEffect, useMemo } from 'react';
import { ACHIEVEMENT_DEFINITIONS, type AchievementRarity } from '@the-wall/shared';
import { useBadgeStore } from '@/stores/badgeStore';
import { AchievementBadgeIcon } from '@/components/AchievementBadgeIcon';

interface UserBadgesProps {
  userId?: string;
  max?: number;
  className?: string;
}

const achievementById = new Map(ACHIEVEMENT_DEFINITIONS.map((definition) => [definition.id, definition]));

function rarityTone(rarity: AchievementRarity): string {
  if (rarity === 'legendary') return 'text-amber-300 border-amber-300/60 bg-amber-400/12';
  if (rarity === 'epic') return 'text-fuchsia-300 border-fuchsia-300/60 bg-fuchsia-400/12';
  if (rarity === 'rare') return 'text-sky-300 border-sky-300/60 bg-sky-400/12';
  if (rarity === 'uncommon') return 'text-emerald-300 border-emerald-300/60 bg-emerald-400/12';
  return 'text-slate-200 border-slate-200/60 bg-slate-200/10';
}

export function UserBadges({ userId, max = 3, className = '' }: UserBadgesProps) {
  const loadBadges = useBadgeStore((state) => state.loadBadges);
  const entry = useBadgeStore((state) => (userId ? state.cache[userId] : undefined));

  useEffect(() => {
    if (!userId) return;
    loadBadges(userId);
  }, [userId, loadBadges]);

  const badges = useMemo(() => {
    if (!entry || entry.status !== 'loaded') return [];
    return entry.badges
      .slice()
      .sort((a, b) => a.slot - b.slot)
      .slice(0, max)
      .map((badge) => ({
        ...badge,
        definition: achievementById.get(badge.id),
      }))
      .filter((badge) => Boolean(badge.definition));
  }, [entry, max]);

  if (badges.length === 0) return null;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {badges.map((badge) => (
        <span
          key={`${badge.id}-${badge.slot}`}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${rarityTone(badge.definition!.rarity)}`}
          title={badge.definition?.title ?? 'Achievement'}
          aria-label={badge.definition?.title ?? 'Achievement badge'}
        >
          <AchievementBadgeIcon achievementId={badge.id} className="h-4 w-4" />
        </span>
      ))}
    </span>
  );
}
